import React from "react";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Divider,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { GenericModal } from "components/GenericModal";

const getFunctionCode = (fn) => (typeof fn === "string" ? fn : fn?.fn_name);

const Section = ({ label, description, children }) => (
    <Box sx={{ py: 2 }}>
        <Typography
            sx={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#86868b",
                fontFamily: '"Nunito", sans-serif',
                mb: description ? "2px" : 1.5,
            }}
        >
            {label}
        </Typography>
        {description && (
            <Typography
                sx={{
                    fontSize: "12px",
                    color: "#86868b",
                    fontFamily: '"Nunito", sans-serif',
                    mb: 1.5,
                }}
            >
                {description}
            </Typography>
        )}
        {children}
    </Box>
);

/**
 * Generic per-row options editor for the leads mapping step: functions,
 * default value, prefix label and comparison management in one dialog.
 */
const FieldOptionsModal = ({
    open,
    onClose,
    item,
    index,
    language,
    allFunctions,
    onFunctionsChange,
    functionValues,
    onFunctionValueChange,
    onDefaultChange,
    onPrefixChange,
    onManageComparisons,
    comparisonDisabled,
    sourceFieldName,
}) => {
    const selectedFunctionCodes = (item?.options?.functions || []).map(
        getFunctionCode,
    );
    const hasFunction = (code) => selectedFunctionCodes.includes(code);

    const comparisonCount = Array.isArray(
        item?.compared_fields?.[item?.sourceKey],
    )
        ? item.compared_fields[item.sourceKey].filter(
              (row) => row?.compare_to || row?.map_to,
          ).length
        : 0;

    const content = (
        <Box sx={{ px: 3, py: 1 }}>
            <Section
                label={language?.lead?.functions ?? "Functions"}
                description={
                    language?.lead?.functions_desc ??
                    "Transform the source value before it is sent to the destination."
                }
            >
                <Autocomplete
                    multiple
                    id={`functions-autocomplete-${index}`}
                    options={allFunctions || []}
                    getOptionLabel={(option) => option.label || option}
                    value={selectedFunctionCodes.map(
                        (val) =>
                            allFunctions?.find((f) => f.value === val) || {
                                label: val,
                                value: val,
                            },
                    )}
                    onChange={(event, newValue) => {
                        onFunctionsChange(newValue.map((opt) => opt.value));
                    }}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value?.value
                    }
                    size="medium"
                    renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                            <Chip
                                variant="outlined"
                                size="small"
                                label={option.label}
                                {...getTagProps({ index: tagIndex })}
                                key={option.value}
                                onClick={() => {}}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={
                                language?.connection_detail?.select_functions ||
                                "Select Functions"
                            }
                        />
                    )}
                    disableCloseOnSelect
                />
                {hasFunction("max_len") && (
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label={
                            language?.connection_detail?.max_len_label ??
                            "Max Length Value"
                        }
                        value={functionValues?.maxLen ?? ""}
                        onChange={(e) =>
                            onFunctionValueChange("maxLen", e?.target?.value)
                        }
                    />
                )}
                {hasFunction("pad_with") && (
                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={
                                language?.connection_detail
                                    ?.pad_with_character_label ??
                                "Pad With Character"
                            }
                            value={functionValues?.padWithCharacter ?? ""}
                            onChange={(e) =>
                                onFunctionValueChange(
                                    "padWithCharacter",
                                    e?.target?.value,
                                )
                            }
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={
                                language?.connection_detail
                                    ?.pad_with_length_label ?? "Pad With Length"
                            }
                            value={functionValues?.padWithLength ?? ""}
                            onChange={(e) =>
                                onFunctionValueChange(
                                    "padWithLength",
                                    e?.target?.value,
                                )
                            }
                        />
                    </Box>
                )}
                {hasFunction("left") && (
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label={"How many characters taken from left"}
                        value={functionValues?.left ?? ""}
                        onChange={(e) =>
                            onFunctionValueChange("left", e?.target?.value)
                        }
                    />
                )}
                {hasFunction("right") && (
                    <TextField
                        fullWidth
                        size="small"
                        sx={{ mt: 2 }}
                        label={"How many characters taken from right"}
                        value={functionValues?.right ?? ""}
                        onChange={(e) =>
                            onFunctionValueChange("right", e?.target?.value)
                        }
                    />
                )}
            </Section>
            <Divider />
            <Section
                label={language?.lead?.default_value ?? "Default Value"}
                description={
                    language?.lead?.default_value_desc ??
                    "Used when the source field has no value."
                }
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder={
                        language?.lead?.your_default_value ??
                        "Your default value"
                    }
                    value={item?.options?.default ?? ""}
                    onChange={(e) => onDefaultChange(e?.target?.value)}
                />
            </Section>
            <Divider />
            <Section
                label={language?.lead?.prefix_label ?? "Prefix Label"}
                description={
                    language?.lead?.prefix_label_desc ??
                    "Added in front of the value that is sent to the destination."
                }
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder={
                        language?.lead?.your_prefix_label ?? "Your prefix label"
                    }
                    value={item?.options?.prefix ?? ""}
                    onChange={(e) => onPrefixChange(e?.target?.value)}
                />
            </Section>
            <Divider />
            <Section
                label={language?.lead?.comparisons ?? "Comparisons"}
                description={
                    language?.lead?.comparisons_desc ??
                    "Replace specific incoming values before sending them to the destination — e.g. City comes as “New York”, send “NWY” instead."
                }
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <Tooltip
                        title={
                            comparisonDisabled
                                ? (language?.lead?.please_select_source_field ??
                                  "Select source field")
                                : ""
                        }
                    >
                        <span>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<CompareArrowsIcon />}
                                disabled={comparisonDisabled}
                                onClick={onManageComparisons}
                            >
                                {comparisonCount > 0
                                    ? (language?.lead?.edit_comparisons ??
                                      "Edit Comparisons")
                                    : (language?.lead?.add_comparisons ??
                                      "Add Comparisons")}
                            </Button>
                        </span>
                    </Tooltip>
                    {comparisonCount > 0 && (
                        <Chip
                            size="small"
                            label={`${comparisonCount} ${
                                comparisonCount === 1 ? "rule" : "rules"
                            }`}
                            onClick={() => {}}
                        />
                    )}
                </Box>
            </Section>
        </Box>
    );

    const footer = (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button color="primary" variant="contained" onClick={onClose}>
                {language?.buttons?.save ?? "Save"}
            </Button>
        </Box>
    );

    return (
        <GenericModal
            open={open}
            onClose={onClose}
            title={language?.lead?.field_options ?? "Field Options"}
            subtitle={sourceFieldName || undefined}
            maxWidth="sm"
            content={content}
            footer={footer}
        />
    );
};

export default FieldOptionsModal;
